import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import {
  Box,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Pagination,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import GroupAddIcon from "@mui/icons-material/GroupAdd";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import ViewListIcon from "@mui/icons-material/ViewList";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import BadgeIcon from "@mui/icons-material/Badge";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import Swal from "sweetalert2";
import { axiosClient } from "../AxiosClient";
import DesplegarCuotasModal from "../cobranza/DesplegarCuotasModal";
import ReasignarPagoModal from "../cobranza/ReasignarPagoModal";
import {
  deployCreditQuotas,
  getDeployCreditQuotasErrorMessage,
} from "../cobranza/deployCreditQuotas";
import { fetchPortfolioClients, PAGE_SIZE, SEARCH_MODES } from "./gestionCarteraApi";
import {
  formatDocument,
  formatFullName,
  formatMoney,
  moraBadge,
  MORA_FILTERS,
} from "./gestionCarteraFormat";
import { clientProfilePath } from "./gestionCarteraRoutes";
import {
  Card,
  cellSx,
  COLORS,
  headCellSx,
  linkSx,
  PageContainer,
  PrimaryButton,
  SecondaryButton,
  SkeletonRows,
  StatusBadge,
  TableMessage,
} from "./gestionCarteraUi";
import ImportarModal from "./modals/ImportarModal";
import ImportarClientesModal from "./modals/ImportarClientesModal";
import FacturaClienteModal from "./modals/FacturaClienteModal";

const SEARCH_DEBOUNCE_MS = 400;
const COLUMNS = ["Cliente", "Celular", "Créditos activos", "Saldo pendiente", "Saldo vencido", "Días de mora", "Acción"];
const VIABILITY_STUDY_PATH = "/homeAdmin/viability-study-by-document";
const DELETE_CREDITS_PATH = "/homeAdmin/eliminar-creditos";

function ActionsBar({ onOpen, navigate }) {
  const buttonSx = { width: { xs: "100%", sm: "auto" }, justifyContent: "center" };
  return (
    <Box component="section" aria-labelledby="gc-acciones" sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
      <Typography id="gc-acciones" component="h2" sx={{ fontSize: 14, fontWeight: 700, color: COLORS.primary }}>
        Acciones
      </Typography>
      <Card sx={{ p: { xs: 1.5, sm: "16px 20px" }, display: "flex", flexWrap: "wrap", gap: 1.5 }}>
        <PrimaryButton startIcon={<UploadFileIcon />} onClick={() => onOpen("importar")} sx={buttonSx}>Importar</PrimaryButton>
        <PrimaryButton startIcon={<GroupAddIcon />} onClick={() => onOpen("importarClientes")} sx={buttonSx}>Importar clientes</PrimaryButton>
        <PrimaryButton startIcon={<FactCheckIcon />} onClick={() => navigate(VIABILITY_STUDY_PATH)} sx={buttonSx}>
          Generar estudio de viabilidad
        </PrimaryButton>
        <PrimaryButton startIcon={<SwapHorizIcon />} onClick={() => onOpen("reasignar")} sx={buttonSx}>Reasignar pago</PrimaryButton>
        <PrimaryButton startIcon={<ViewListIcon />} onClick={() => onOpen("desplegar")} sx={buttonSx}>Desplegar cuotas</PrimaryButton>
        <PrimaryButton
          startIcon={<DeleteIcon />}
          onClick={() => navigate(DELETE_CREDITS_PATH)}
          sx={{ ...buttonSx, backgroundColor: COLORS.dangerSolid, "&:hover": { backgroundColor: "#C93C34" } }}
        >
          Eliminar crédito
        </PrimaryButton>
      </Card>
    </Box>
  );
}

/**
 * Dos modos de búsqueda sobre número de documento y nombre:
 * - Mientras se escribe: COINCIDENCIAS (contiene el texto), con espera de SEARCH_DEBOUNCE_MS.
 * - Botón "Buscar" o Enter: coincidencia EXACTA de documento o nombre completo.
 */
function SearchFilters({ query, onQuery, onSearch, onClear }) {
  const handleSubmit = (event) => {
    event.preventDefault();
    onSearch();
  };

  return (
    <Card
      component="form"
      role="search"
      aria-label="Búsqueda"
      onSubmit={handleSubmit}
      sx={{ p: { xs: 1.5, sm: 2.5 }, display: "flex", flexWrap: "wrap", alignItems: "flex-start", gap: 2 }}
    >
      <TextField
        label="Número de documento o nombre completo"
        placeholder="Ej. 1032456789 o Camila Martínez Restrepo"
        helperText="Al escribir muestra coincidencias. Con Buscar o Enter busca el valor exacto."
        value={query}
        onChange={(event) => onQuery(event.target.value)}
        sx={{ flex: { xs: "1 1 100%", sm: "1 1 360px" }, minWidth: 0 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start"><SearchIcon /></InputAdornment>
          ),
          endAdornment: query && (
            <InputAdornment position="end">
              <IconButton aria-label="Limpiar búsqueda" onClick={onClear}><CloseIcon /></IconButton>
            </InputAdornment>
          ),
        }}
      />
      <PrimaryButton type="submit" startIcon={<SearchIcon />} sx={{ minHeight: 56, px: 3, width: { xs: "100%", sm: "auto" } }}>
        Buscar
      </PrimaryButton>
    </Card>
  );
}

function ClientRow({ client, onInvoice }) {
  const badge = moraBadge(client.moraDays);
  const profilePath = clientProfilePath(client.uuid);
  return (
    <TableRow hover>
      <TableCell sx={cellSx}>
        <Box component={RouterLink} to={profilePath} sx={linkSx}>{formatFullName(client)}</Box>
        <Typography sx={{ fontSize: 14, color: COLORS.muted }}>{formatDocument(client)}</Typography>
      </TableCell>
      <TableCell sx={cellSx}>{client.cellphone || "—"}</TableCell>
      <TableCell sx={cellSx}>{client.activeCredits}</TableCell>
      <TableCell sx={cellSx}>{formatMoney(client.outstandingBalance)}</TableCell>
      <TableCell sx={{ ...cellSx, color: client.overdueBalance > 0 ? COLORS.danger : COLORS.muted, fontWeight: 500 }}>
        {formatMoney(client.overdueBalance)}
      </TableCell>
      <TableCell sx={cellSx}><StatusBadge tone={badge.tone} label={badge.label} /></TableCell>
      <TableCell sx={cellSx}>
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Tooltip title="Factura">
            <IconButton aria-label={`Factura de ${formatFullName(client)}`} onClick={() => onInvoice(client)} sx={{ color: COLORS.primary }}>
              <ReceiptLongOutlinedIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Ver cliente">
            <IconButton component={RouterLink} to={profilePath} aria-label="Ver cliente" sx={{ color: COLORS.primary }}>
              <BadgeIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </TableCell>
    </TableRow>
  );
}

function ClientsTableBody({ status, rows, query, onRetry, onClear, onInvoice }) {
  if (status === "loading") return <SkeletonRows columns={COLUMNS.length} rows={7} />;
  if (status === "error") {
    return (
      <TableMessage
        columns={COLUMNS.length}
        role="alert"
        title="No pudimos cargar los clientes"
        description="Revisa tu conexión a internet e inténtalo de nuevo."
        action={<SecondaryButton onClick={onRetry}>Reintentar</SecondaryButton>}
      />
    );
  }
  if (rows.length === 0) {
    return (
      <TableMessage
        columns={COLUMNS.length}
        title={query.trim() ? `Sin resultados para '${query.trim()}'` : "Sin resultados para este filtro"}
        description="Revisa el nombre o el número de documento, o cambia el filtro."
        action={query.trim() && <SecondaryButton onClick={onClear}>Limpiar búsqueda</SecondaryButton>}
      />
    );
  }
  return rows.map((client) => <ClientRow key={client.uuid} client={client} onInvoice={onInvoice} />);
}

/** Pantalla 1 · Gestión Cartera: listado de clientes con su estado de cartera. */
export default function GestionCartera() {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  // Cada búsqueda crea un objeto nuevo, así repetir la misma búsqueda también recarga.
  const [appliedSearch, setAppliedSearch] = useState({ query: "", mode: SEARCH_MODES.contains });
  const typingTimer = useRef(null);
  const [moraFilter, setMoraFilter] = useState("todos");
  const [order, setOrder] = useState("desc");
  const [page, setPage] = useState(0);
  const [result, setResult] = useState({ status: "loading", rows: [], totalElements: 0 });
  const [openModal, setOpenModal] = useState(null);
  const [invoiceClient, setInvoiceClient] = useState(null);
  const [deployState, setDeployState] = useState({ loading: false, error: "" });
  const latestRequest = useRef(0);

  const loadClients = useCallback(async () => {
    const requestId = ++latestRequest.current;
    setResult((current) => ({ ...current, status: "loading" }));
    try {
      const data = await fetchPortfolioClients({ page, ...appliedSearch, moraFilter, order });
      if (requestId === latestRequest.current) setResult({ status: "ready", ...data });
    } catch (error) {
      if (requestId === latestRequest.current) setResult({ status: "error", rows: [], totalElements: 0 });
    }
  }, [page, appliedSearch, moraFilter, order]);

  useEffect(() => {
    loadClients();
  }, [loadClients]);

  useEffect(() => {
    setPage(0);
  }, [appliedSearch, moraFilter, order]);

  useEffect(() => () => clearTimeout(typingTimer.current), []);

  /** Al escribir: búsqueda por coincidencias cuando el usuario deja de teclear. */
  const handleQueryChange = (value) => {
    setQuery(value);
    clearTimeout(typingTimer.current);
    typingTimer.current = setTimeout(
      () => setAppliedSearch({ query: value.trim(), mode: SEARCH_MODES.contains }),
      SEARCH_DEBOUNCE_MS
    );
  };

  /** Botón "Buscar" o Enter: búsqueda exacta inmediata (cancela la de coincidencias pendiente). */
  const handleSearch = () => {
    clearTimeout(typingTimer.current);
    setAppliedSearch({ query: query.trim(), mode: SEARCH_MODES.exact });
  };

  const handleClearSearch = () => {
    clearTimeout(typingTimer.current);
    setQuery("");
    setAppliedSearch({ query: "", mode: SEARCH_MODES.contains });
  };

  const handleDeployQuotas = async (oldId) => {
    setDeployState({ loading: true, error: "" });
    try {
      const approvedCredit = await deployCreditQuotas(axiosClient, oldId);
      setDeployState({ loading: false, error: "" });
      setOpenModal(null);
      loadClients();
      Swal.fire({
        icon: "success",
        title: "Cuotas desplegadas",
        text: `Se desplegaron correctamente las cuotas del crédito ${approvedCredit?.oldId ?? oldId}.`,
        confirmButtonColor: COLORS.primary,
      });
    } catch (error) {
      setDeployState({ loading: false, error: getDeployCreditQuotasErrorMessage(error) });
    }
  };

  const closeModal = () => {
    if (deployState.loading) return;
    setDeployState({ loading: false, error: "" });
    setOpenModal(null);
  };

  const totalPages = Math.max(Math.ceil(result.totalElements / PAGE_SIZE), 1);
  const firstRow = result.totalElements === 0 ? 0 : page * PAGE_SIZE + 1;
  const lastRow = Math.min((page + 1) * PAGE_SIZE, result.totalElements);

  return (
    <PageContainer>
      <ActionsBar onOpen={setOpenModal} navigate={navigate} />

      <Box>
        <Typography component="h1" sx={{ fontSize: 14, fontWeight: 700, color: COLORS.primary }}>Clientes</Typography>
        <Typography sx={{ mt: 0.75, color: COLORS.muted, fontSize: 14, fontWeight: 500 }}>
          Consulta clientes y gestiona las operaciones de recaudo
        </Typography>
      </Box>

      <SearchFilters query={query} onQuery={handleQueryChange} onSearch={handleSearch} onClear={handleClearSearch} />

      <Card component="section" aria-label="Listado de clientes" sx={{ overflow: "hidden" }}>
        <Box sx={{ display: "flex", flexWrap: "wrap", justifyContent: "flex-end", alignItems: "center", gap: 1, p: { xs: "12px", sm: "16px 20px" } }}>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, width: { xs: "100%", sm: "auto" } }}>
            <FormControl size="small" sx={{ minWidth: { xs: 0, sm: 180 }, width: { xs: "100%", sm: "auto" } }}>
              <InputLabel id="gc-mora">Estado de mora</InputLabel>
              <Select labelId="gc-mora" label="Estado de mora" value={moraFilter} onChange={(event) => setMoraFilter(event.target.value)}>
                {MORA_FILTERS.map((filter) => (
                  <MenuItem key={filter.value} value={filter.value}>{filter.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: { xs: 0, sm: 260 }, width: { xs: "100%", sm: "auto" } }}>
              <InputLabel id="gc-orden">Orden</InputLabel>
              <Select labelId="gc-orden" label="Orden" value={order} onChange={(event) => setOrder(event.target.value)}>
                <MenuItem value="desc">Días de mora: mayor a menor</MenuItem>
                <MenuItem value="asc">Días de mora: menor a mayor</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Box>
        <TableContainer sx={{ overflowX: "auto" }}>
          <Table sx={{ minWidth: 960 }} aria-label="Clientes">
            <TableHead>
              <TableRow>
                {COLUMNS.map((column) => <TableCell key={column} sx={headCellSx}>{column}</TableCell>)}
              </TableRow>
            </TableHead>
            <TableBody>
              <ClientsTableBody
                status={result.status}
                rows={result.rows}
                query={appliedSearch.query}
                onRetry={loadClients}
                onClear={handleClearSearch}
                onInvoice={setInvoiceClient}
              />
            </TableBody>
          </Table>
        </TableContainer>
        <Box sx={{ display: "flex", flexWrap: "wrap", justifyContent: { xs: "center", sm: "space-between" }, alignItems: "center", gap: 1.5, p: { xs: "12px", sm: "14px 20px" } }}>
          <Typography sx={{ color: COLORS.muted }}>
            {`Mostrando ${firstRow}–${lastRow} de ${result.totalElements} clientes`}
          </Typography>
          <Pagination
            count={totalPages}
            page={page + 1}
            onChange={(_, newPage) => setPage(newPage - 1)}
            shape="rounded"
            size="small"
            siblingCount={0}
            aria-label="Paginación"
          />
        </Box>
      </Card>

      <ImportarModal open={openModal === "importar"} onClose={closeModal} />
      <ImportarClientesModal open={openModal === "importarClientes"} onClose={closeModal} />
      <FacturaClienteModal open={Boolean(invoiceClient)} client={invoiceClient} onClose={() => setInvoiceClient(null)} />
      <ReasignarPagoModal open={openModal === "reasignar"} onClose={closeModal} />
      <DesplegarCuotasModal
        open={openModal === "desplegar"}
        loading={deployState.loading}
        errorMessage={deployState.error}
        onClose={closeModal}
        onSubmit={handleDeployQuotas}
      />
    </PageContainer>
  );
}
